import { KeyboardBackspace as Arrow } from '@mui/icons-material';
import { NoSsr } from '@mui/material';
import {
  CategoryTypes,
  type HeadlineResponse,
  type LatestForumPostsResponse,
  type OrgCompositeRankingResRelatedRankingResponseRelatedPagedData,
  type PersonSportRankingResponseRelatedRankingResponseRelatedPagedData,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { MobileMPU } from 'components/Ads/MobileMPU';
import { MPU } from 'components/Ads/MPU';
import { PostFeed } from 'components/Forums/Forums';
import { CoverList } from 'components/Headlines/ArticleCover/CoverList';
import { FeedList } from 'components/Headlines/ArticleFeed/FeedList';
import { TopRankings } from 'components/HighSchool/Modules/TopRankings/TopRankings';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { SignUpFooter } from 'components/Newsletter/SignUpFooter';
import { SignUpModule } from 'components/Newsletter/SignUpModule';
import { PlayerRankingsModule } from 'components/Player/RankingsModule';
import { PianoPromo } from 'components/Promos/PianoPromo';
import { TeamConnect } from 'components/Social/TeamConnect';
import { TopHeadlines } from 'components/TopHeadlines';
import { useMemo } from 'react';
import { getArticleLists } from 'utils/articles';
import { createTitle } from 'utils/meta';
import { hasSiteSportCategory } from 'utils/sites';

import styles, { neutral30, neutral70 } from './HighSchoolHome.module.scss';

export interface IHighSchoolHomeProps {
  featuredArticles: HeadlineResponse[] | null;
  trendingList: TrendingListItemResponseTrendingListResponseRelatedPagedData | null;
  newsArticles: HeadlineResponse[] | null;
  siteForums: LatestForumPostsResponse | null;
  topPlayerRankings: PersonSportRankingResponseRelatedRankingResponseRelatedPagedData | null;
  topTeamRankings: OrgCompositeRankingResRelatedRankingResponseRelatedPagedData | null;
}

export const HighSchoolHome = ({
  featuredArticles,
  trendingList,
  newsArticles,
  siteForums,
  topPlayerRankings,
  topTeamRankings,
}: IHighSchoolHomeProps) => {
  const { currentSite } = useSite();

  const { siteName, url: siteUrl } = currentSite || {};

  const currentState = topPlayerRankings?.relatedModel?.state;
  const siteCategories = useMemo(
    () => currentSite?.siteCategories || [],
    [currentSite],
  );

  const [featuredArticlesList, newsArticlesList] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 3,
    },
    lists: [
      {
        list: newsArticles ?? [],
        sliceLimit: 10,
        removeDuplicates: true,
      },
    ],
  });

  const newsViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'football' },
        { sport: 'Basketball', slug: 'basketball' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.Sport, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport}`,
          title: `${sport} News`,
        })),
    [siteUrl, siteCategories],
  );

  return (
    <>
      {!!featuredArticlesList?.length && (
        <CoverList
          authorColor={neutral30}
          backgroundColor={neutral70}
          categoryColor={neutral30}
          list={featuredArticlesList}
          titleColor="#fff"
        >
          <NoSsr>
            <PianoPromo id="pianoPromoDark" />
          </NoSsr>
        </CoverList>
      )}

      <HomeLayout className={styles.layout}>
        <section className={styles.center}>
          <div className={styles.inner}>
            <div className={styles.recruits}>
              <Link className={styles.recruitsLink} href="/recruits/join/">
                <ServiceLogo
                  className={styles.recruitLogo}
                  isAlt
                  type="recruits"
                />
                <span>Get a profile. Be recruited.</span>
                <Arrow className={styles.arrow} />
              </Link>
            </div>

            <div className={styles.articleLists}>
              {!!newsArticlesList?.length && (
                <FeedList
                  list={newsArticlesList}
                  section={createTitle(currentSite.name, 'News')}
                  viewAllLinks={newsViewAll}
                />
              )}

              {!!siteForums?.posts?.length && (
                <PostFeed
                  forum={siteForums?.primaryForum}
                  // links={forumViewAll}
                  posts={siteForums?.posts}
                  title="Popular Message Board Posts"
                />
              )}
            </div>
          </div>
        </section>
        <aside className={styles.right}>
          <div className={styles.inner}>
            <MPU id={'mpu_top'} />

            {!!topPlayerRankings && (
              <PlayerRankingsModule
                isModule
                playerRankings={topPlayerRankings}
                title={createTitle(
                  'Top Players',
                  currentState?.name && `in ${currentState.name}`,
                )}
              />
            )}

            <MPU id={'mpu_middle'} />
            <MobileMPU id={'mobile_mpu_middle'} incontent />
          </div>
        </aside>
        <aside className={styles.left}>
          <div className={styles.inner}>
            {!!topTeamRankings && (
              <TopRankings isModule rankings={topTeamRankings} />
            )}

            {!!trendingList && (
              <div className={styles.trending}>
                <TopHeadlines data={trendingList} />
                <SignUpModule title="Get the Daily On3 Newsletter in your inbox every morning" />
              </div>
            )}

            <TeamConnect />
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter titleText={`${siteName} Newsletter`} />
    </>
  );
};
